import React from "react";
import { useMoney } from "../../../hooks/useMoney";
import { Plus, Trash2, Calendar } from "lucide-react";
import type { DistributorProgram } from "../../../features/distributor/DistributorSlice";
import type { YearEndDiscountRange } from "./YearEndModal";
import { nextCalendarDay, toDateInputValue, getTodayDateInputValue } from "./yearEndUtils";

export type YearEndFormStep = 1 | 2;

interface YearEndFormSectionProps {
  step: YearEndFormStep;
  program: DistributorProgram;
  currentFYStartDisplay: string;
  currentFYEndDate: string;
  onCurrentFYEndDateChange: (value: string) => void;
  newFYStartDisplay: string;
  newFYEndDate: string;
  onNewFYEndDateChange: (value: string) => void;
  remark: string;
  onRemarkChange: (value: string) => void;
  discountRanges: YearEndDiscountRange[];
  onDiscountChange: (index: number, field: keyof YearEndDiscountRange, value: string) => void;
  onAddRange: () => void;
  onRemoveRange: (index: number) => void;
}

const YearEndFormSection: React.FC<YearEndFormSectionProps> = ({
  step,
  program,
  currentFYStartDisplay,
  currentFYEndDate,
  onCurrentFYEndDateChange,
  newFYStartDisplay,
  newFYEndDate,
  onNewFYEndDateChange,
  remark,
  onRemarkChange,
  discountRanges,
  onDiscountChange,
  onAddRange,
  onRemoveRange,
}) => {
  const { symbol } = useMoney();
  const wallet = program.ProgramDistributorWallet;
  const today = getTodayDateInputValue();
  const currentFyStart = wallet?.contractStartDate ? toDateInputValue(wallet.contractStartDate) : "";
  const currentFyMin = currentFyStart ? nextCalendarDay(currentFyStart) : undefined;
  const contractEnd = wallet?.contractEndDate ? toDateInputValue(wallet.contractEndDate) : null;
  const currentFyMax = contractEnd ? (contractEnd < today ? contractEnd : today) : today;
  const newFyStart = currentFYEndDate ? nextCalendarDay(currentFYEndDate) : "";
  const newFyMin = newFyStart ? nextCalendarDay(newFyStart) : undefined;
  const step1Content = (
    <div className="space-y-6">
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-gray-50 border border-gray-200">
        <span className="text-sm text-gray-600">Close FY for</span>
        <span className="font-semibold text-gray-900">{program.programName}</span>
        <span className="text-xs text-gray-500 bg-gray-200/80 px-2 py-0.5 rounded">Program Distributor</span>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-1.5 rounded-lg bg-brand/10">
            <Calendar className="w-4 h-4 text-brand" />
          </div>
          <h4 className="text-sm font-semibold text-gray-800">Financial year dates</h4>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-4 rounded-lg bg-gray-50/80 p-4 border border-gray-100">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Current FY</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start (default)</label>
              <div className="px-3 py-2.5 rounded-md bg-white border border-gray-200 text-sm text-gray-800 font-medium">
                {currentFYStartDisplay}
              </div>
              <p className="text-xs text-gray-400 mt-1">Existing contract start</p>
            </div>
            <div>
              <label htmlFor="current-fy-end" className="block text-xs font-medium text-gray-500 mb-1">End date</label>
              <input
                id="current-fy-end"
                type="date"
                value={currentFYEndDate}
                onChange={(e) => onCurrentFYEndDateChange(e.target.value)}
                min={currentFyMin}
                max={currentFyMax}
                className="block w-full px-3 py-2.5 rounded-md border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              />
            </div>
          </div>

          <div className="space-y-4 rounded-lg bg-gray-50/80 p-4 border border-gray-100">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">New FY</p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Start (auto)</label>
              <div className="px-3 py-2.5 rounded-md bg-white border border-gray-200 text-sm text-gray-800 font-medium">
                {newFYStartDisplay}
              </div>
              <p className="text-xs text-gray-400 mt-1">Next day after current FY end</p>
            </div>
            <div>
              <label htmlFor="new-fy-end" className="block text-xs font-medium text-gray-500 mb-1">End date</label>
              <input
                id="new-fy-end"
                type="date"
                value={newFYEndDate}
                onChange={(e) => onNewFYEndDateChange(e.target.value)}
                min={newFyMin}
                className="block w-full px-3 py-2.5 rounded-md border border-gray-300 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const step2Content = (
    <div className="space-y-6">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Remark (optional)</h4>
        <input
          id="year-end-remark"
          type="text"
          value={remark}
          onChange={(e) => onRemarkChange(e.target.value)}
          placeholder="e.g. Year-end closure note"
          className="block w-full px-3 py-2.5 rounded-md border border-gray-300 bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
        />
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-gray-800">Existing discount structure</h4>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">Reference only</span>
        </div>
        {program.ProgramDistributorWallet?.discountRanges?.length ? (
          <div className="space-y-2">
            {program.ProgramDistributorWallet.discountRanges.map((range, index) => (
              <div
                key={index}
                className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-100 text-sm"
              >
                <span className="text-gray-700">
                  {symbol}{range.min_order_value.toLocaleString()} – {range.max_order_value == null ? "∞" : `${symbol}${range.max_order_value.toLocaleString()}`}
                </span>
                <span className="font-semibold text-brand">{range.discount}% discount</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-3 rounded-lg bg-gray-50 text-sm text-gray-500 italic border border-gray-100">
            No discount structure in current FY.
          </div>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-800 mb-4">New discount structure for upcoming FY</h4>

        <div className="hidden sm:grid sm:grid-cols-[1fr_1fr_100px_44px] gap-3 mb-2 px-1">
          <span className="text-xs font-medium text-gray-500">Min value ({symbol})</span>
          <span className="text-xs font-medium text-gray-500">Max value ({symbol})</span>
          <span className="text-xs font-medium text-gray-500">Discount %</span>
          <span className="text-xs font-medium text-gray-500 text-right">Action</span>
        </div>

        <div className="space-y-3">
          {discountRanges.map((range, index) => (
            <div
              key={index}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_100px_44px] gap-3 items-center p-3 rounded-lg bg-gray-50/80 border border-gray-100"
            >
              <div>
                <label className="sm:sr-only text-xs text-gray-500 mb-0.5 sm:mb-0">Min ({symbol})</label>
                <input
                  type="number"
                  min={0}
                  value={range.min_order_value}
                  onChange={(e) => onDiscountChange(index, "min_order_value", e.target.value)}
                  placeholder="0"
                  className="block w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="sm:sr-only text-xs text-gray-500 mb-0.5 sm:mb-0">Max ({symbol})</label>
                <input
                  type="number"
                  min={0}
                  value={range.max_order_value ?? ""}
                  onChange={(e) => onDiscountChange(index, "max_order_value", e.target.value)}
                  placeholder="∞"
                  className="block w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="sm:sr-only text-xs text-gray-500 mb-0.5 sm:mb-0">Discount %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={range.discount ?? ""}
                  onChange={(e) => onDiscountChange(index, "discount", e.target.value)}
                  placeholder="%"
                  className="block w-full px-3 py-2 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div className="flex justify-end sm:justify-center">
                <button
                  type="button"
                  onClick={() => onRemoveRange(index)}
                  disabled={discountRanges.length <= 1}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Remove range"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onAddRange}
          className="mt-4 flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-brand border-2 border-dashed border-brand/50 rounded-lg hover:bg-brand/5 hover:border-brand transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add range
        </button>
      </section>
    </div>
  );

  return step === 1 ? step1Content : step2Content;
};

export default YearEndFormSection;
